import { lazy, useMemo } from 'react';
import {
  type RouteObject,
  RouterProvider,
  createBrowserRouter
} from 'react-router';

import AppProvider from './providers/app.provider';
import AuthorizedProvider from './providers/authorized.provider';
import Layout from './providers/layout/layout';
import ModuleRoute from './providers/module.route';
import ProtectedRoute from './providers/protected.route';
import SessionProvider from './providers/session.provider';
import StartRedirect from './providers/start.redirect';
import SuspenseRoute from './providers/suspense-route/suspense.route';
import { ROUTES, type TModuleCode } from './shared/routes';
import NotFound from './shared/widgets/errors/not-found';

const LoginPage = lazy(() => import('./pages/Auth/Login/Login'));
const LeasingScoringPage = lazy(
  () => import('./pages/LeasingScoring/LeasingScoring')
);
const LeasingScoringRequestPage = lazy(
  () => import('./pages/LeasingScoringRequest/LeasingScoringRequest')
);

// Разделы без API открываются только списком — пустым, если бэкенд пришлёт их модуль.
// Карточки, формы этих разделов и создание заявки на лизинг остаются на моках без
// роутов — docs/layout.md, «Скрыто до согласования».
const ContractsPage = lazy(() => import('./pages/Contracts/Contracts'));
const LeasedPropertyPage = lazy(
  () => import('./pages/LeasedProperty/LeasedProperty')
);
const ClientsPage = lazy(() => import('./pages/Clients/Clients'));
const ClientAppealsPage = lazy(
  () => import('./pages/ClientContractAppeals/ClientContractAppeals')
);
const AssessmentsPage = lazy(() => import('./pages/Assessments/Assessments'));
const ReportsPage = lazy(() => import('./pages/Reports/Reports'));

/** Роуты раздела: открыты, только если модуль раздела есть у сотрудника. */
const moduleRoutes = (
  module: TModuleCode,
  children: RouteObject[]
): RouteObject => ({
  element: <ModuleRoute module={module} />,
  children: [{ element: <SuspenseRoute />, children }]
});

const App: React.FC = () => {
  const router = useMemo(() => {
    return createBrowserRouter([
      {
        path: '/',
        element: <AppProvider />,
        children: [
          {
            path: '/:lang',
            element: <AuthorizedProvider />,
            children: [
              // Открыта только страница входа — остальной кабинет за авторизацией.
              {
                element: <SuspenseRoute />,
                children: [
                  {
                    path: ROUTES.login,
                    element: <LoginPage />
                  }
                ]
              },

              {
                // Закрыты: требуют авторизации и загруженной сессии сотрудника.
                element: <ProtectedRoute />,
                children: [
                  {
                    element: <SessionProvider />,
                    children: [
                      {
                        // С общей шапкой и навигацией.
                        element: <Layout />,
                        children: [
                          { index: true, element: <StartRedirect /> },

                          moduleRoutes('autocredit-requests', [
                            {
                              path: ROUTES.leasingScoring,
                              element: <LeasingScoringPage />
                            },
                            {
                              path: ROUTES.leasingScoringRequest(),
                              element: <LeasingScoringRequestPage />
                            }
                          ]),

                          moduleRoutes('contracts', [
                            {
                              path: ROUTES.contracts,
                              element: <ContractsPage />
                            }
                          ]),

                          moduleRoutes('collateral-property', [
                            {
                              path: ROUTES.leasedProperty,
                              element: <LeasedPropertyPage />
                            }
                          ]),

                          moduleRoutes('clients', [
                            { path: ROUTES.clients, element: <ClientsPage /> }
                          ]),

                          moduleRoutes('client-contract-appeals', [
                            {
                              path: ROUTES.clientAppeals,
                              element: <ClientAppealsPage />
                            }
                          ]),

                          moduleRoutes('assessments', [
                            {
                              path: ROUTES.assessments,
                              element: <AssessmentsPage />
                            }
                          ]),

                          moduleRoutes('reports', [
                            { path: ROUTES.reports, element: <ReportsPage /> }
                          ]),

                          { path: '*', element: <NotFound /> }
                        ]
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            path: '*',
            element: <NotFound />
          }
        ]
      }
    ]);
  }, []);

  return <RouterProvider router={router} />;
};

export default App;
